import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ArrowRight, Users, Target, Briefcase, Globe, ChevronDown } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { api } from "@/lib/api";

const HERO = "https://customer-assets-jai6qajn.emergentagent.net/job_premium-coach-16/artifacts/42y1w95p_748939148_1466523908849722_3647474206142775754_n.jpg";

const services = [
  { icon: Target, title: "Coaching individuale", desc: "Percorsi 1:1 per sbloccare obiettivi personali e professionali." },
  { icon: Users, title: "Percorsi personalizzati", desc: "Programmi su misura, costruiti attorno alle tue esigenze." },
  { icon: Briefcase, title: "Corsi aziendali", desc: "Formazione su vendita, marketing e organizzazione per team." },
  { icon: Globe, title: "Formazione web & social", desc: "Costruisci un personal brand efficace e autentico online." },
];

const method = [
  { title: "Ricalco", desc: "Entro in sintonia con te: ascolto, osservo e ricalco il tuo modo di essere, per costruire una fiducia autentica da cui partire." },
  { title: "Consapevolezza", desc: "Portiamo alla luce schemi, risorse e obiettivi. Le domande giuste aprono strade che prima non riuscivi a vedere." },
  { title: "Allenamento a intensità aumentata", desc: "Ti alleno con esercizi mirati e progressivi: alziamo gradualmente l'intensità per rendere il cambiamento stabile e duraturo." },
];

export const Home = () => {
  const { data: events = [] } = useQuery({ queryKey: ["events"], queryFn: () => api.get("/events").then((r) => r.data) });

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-screen flex items-center" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO} alt="Mirko Scravaglieri" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/75 to-black/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 w-full pt-24">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="eyebrow mb-6">
            Mental Coach · Formatore
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }} className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.05] text-white max-w-4xl">
            Trasforma il tuo <span className="gold-gradient italic">potenziale</span> in risultati concreti.
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.25 }} className="mt-8 text-lg text-zinc-300 max-w-xl leading-relaxed">
            Accompagno persone e aziende in percorsi di crescita autentici, per ritrovare chiarezza, energia e direzione.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.4 }} className="mt-10 flex flex-wrap gap-4">
            <Link to="/contatti" data-testid="hero-cta-primary" className="group inline-flex items-center gap-2 rounded-full bg-[#C19A5B] text-black font-semibold px-7 py-3.5 hover:bg-[#D4AF37] transition-colors">
              Primo colloquio gratuito
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/servizi" data-testid="hero-cta-secondary" className="inline-flex items-center gap-2 rounded-full border border-[#C19A5B]/60 text-[#C19A5B] px-7 py-3.5 hover:bg-[#C19A5B]/10 transition-colors">
              Scopri i servizi
            </Link>
          </motion.div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 hidden sm:flex flex-col items-center gap-2 text-zinc-400">
          <span className="text-[10px] tracking-[0.3em] uppercase">Scorri</span>
          <ChevronDown size={20} className="scroll-cue text-[#C19A5B]" />
        </div>
      </section>

      {/* INTRO / stats */}
      <section className="relative z-10 bg-[#0A0A0A] py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-3 gap-10">
          {[["11", "Anni di esperienza"], ["3.000+", "Persone formate"], ["200+", "Aziende accompagnate"]].map(([n, l], i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className="border-l border-[#C19A5B]/40 pl-6">
                <p className="font-display text-5xl text-white">{n}</p>
                <p className="text-zinc-400 mt-2">{l}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="relative z-10 bg-[#0A0A0A] py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal>
            <p className="eyebrow mb-4">Cosa offro</p>
            <h2 className="font-display text-4xl sm:text-5xl text-white max-w-2xl">Percorsi pensati per te, o per il tuo team.</h2>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
            {services.map((s, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div data-testid={`home-service-${i}`} className="hover-lift h-full bg-[#121212] border border-white/10 rounded-xl p-7">
                  <div className="h-12 w-12 rounded-full bg-[#C19A5B]/15 flex items-center justify-center text-[#C19A5B] mb-5">
                    <s.icon size={22} />
                  </div>
                  <h3 className="text-xl text-white mb-3">{s.title}</h3>
                  <p className="text-zinc-400 text-sm leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* METODO */}
      <section className="relative z-10 bg-[#0A0A0A] py-24 border-t border-white/5" data-testid="method-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal>
            <p className="eyebrow mb-4">Come lavoro</p>
            <h2 className="font-display text-4xl sm:text-5xl text-white max-w-2xl">Un metodo in tre fasi.</h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-10 mt-16">
            {method.map((m, i) => (
              <Reveal key={i} delay={i * 0.14}>
                <div className="relative border-t border-[#C19A5B]/30 pt-6" data-testid={`method-step-${i}`}>
                  <span className="font-display text-6xl gold-gradient leading-none">0{i + 1}</span>
                  <h3 className="text-xl text-white mt-5 mb-3 leading-snug">{m.title}</h3>
                  <p className="text-zinc-400 leading-relaxed">{m.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* EVENTS preview */}
      {events.length > 0 && (
        <section className="relative z-10 bg-[#0A0A0A] py-24 border-t border-white/5">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
              <Reveal>
                <p className="eyebrow mb-4">Eventi & Formazioni</p>
                <h2 className="font-display text-4xl sm:text-5xl text-white">Sul palco, tra le persone.</h2>
              </Reveal>
              <Link to="/eventi" className="text-[#C19A5B] inline-flex items-center gap-2 hover:gap-3 transition-all" data-testid="home-events-link">
                Vedi la galleria <ArrowRight size={18} />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {events.slice(0, 6).map((e, i) => (
                <Reveal key={e.id} delay={i * 0.05}>
                  <div className={`img-zoom relative rounded-xl overflow-hidden border border-white/10 ${i === 0 ? "md:col-span-2 md:row-span-2" : ""}`}>
                    <img src={e.image_url} alt={e.title} loading="lazy" decoding="async" className={`w-full object-cover ${i === 0 ? "h-64 md:h-full" : "h-48"}`} />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                    <div className="absolute bottom-0 p-4">
                      <p className="text-white font-medium">{e.title}</p>
                      <p className="text-[#C19A5B] text-xs">{e.location} · {e.event_date}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="relative z-10 bg-[#0A0A0A] py-28">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Reveal>
            <h2 className="font-display text-4xl sm:text-5xl text-white leading-tight">Pronto a scrivere il tuo prossimo capitolo?</h2>
            <p className="text-zinc-400 mt-6 max-w-xl mx-auto">Prenota un primo colloquio gratuito e senza impegno. Parliamo dei tuoi obiettivi.</p>
            <Link to="/contatti" data-testid="cta-bottom" className="mt-9 inline-flex items-center gap-2 rounded-full bg-[#C19A5B] text-black font-semibold px-8 py-4 hover:bg-[#D4AF37] transition-colors">
              Prenota ora <ArrowRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
